import { Box, SxProps, Typography, Divider } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import { lazy, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import defaultServiceImage from "../../../../assets/svg/defaultService.svg";
import defaultEmployeeImage from "../../../../assets/svg/defaultUser.svg";
import { IServiceStore } from "../../../../services/api/ServiceStore/types";
import TimeRangeInterpreter from "../../../../components/comercial/TimeRangeInterpreter";
const CustomCard = lazy(
() => import("../../../../components/general/CustomCard")
);
import ClientNavbar from "../../../../components/general/Navbars/ClientNavbar";
import { IEmployee } from "../../../../services/api/Employee/types";
import { IStore } from "../../../../services/api/Store/types";
import { priceFormatter } from "../../../../utils/priceFormatter";
import {
fetchEmployeesFromStore,
fetchServiceStoresFromStore,
fetchStore,
} from "../../../../utils/fetchEntities";
import { sharedStyles } from "@/styles/shared";
import CollapseWrapper from "@/components/general/Tiptap/RichTextDescriptionWrapper";
import RichTextRenderer from "@/components/general/Tiptap/RichTextRenderer";
const ContactIcon = lazy(
() => import("../../../../components/general/ContactIcon")
);
import NearMeIcon from "@mui/icons-material/NearMe";
import BackPageButton, {
    backButtonContainer,
} from "@/components/general/buttons/BackPageButton";
import { useTranslation } from "react-i18next";
import ImageGalleryContainerApi from "@/components/general/Images/ImageGalleryContainerApi";
import imagesApi from "../../../../services/api/Image";
import { IImage } from "../../../../services/api/Image/types";
import { isValidCoordinatesPair } from "../../../../utils/calculateDistanceBetweenCoordinates";

const StorePage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedStoreId = searchParams.get("id");
  const navigate = useNavigate();
  const [store, setStore] = useState<IStore | null>(null);

  const [selectedStoreServiceStores, setSelectedStoreServiceStores] = useState<IServiceStore[]>([]);
  const [selectedStoreEmployees, setSelectedStoreEmployees] = useState<IEmployee[]>([]);

  const [employeeImages, setEmployeeImages] = useState<Record<string, IImage>>({});

useEffect(() => {
    const fetchStoreData = async () => {
    await Promise.all([
        fetchStore(selectedStoreId!).then((response) => setStore(response)),
        fetchServiceStoresFromStore(selectedStoreId!).then((response) =>
        setSelectedStoreServiceStores(response ?? [])
        ),
        fetchEmployeesFromStore(selectedStoreId!, setSelectedStoreEmployees!),
]);
    };

        if (selectedStoreId) {
            fetchStoreData();
        }
    }, [selectedStoreId]);

    useEffect(() => {
        if (!selectedStoreEmployees || selectedStoreEmployees.length === 0) return;
        Promise.all(
            selectedStoreEmployees.map(async (employee) => {
                const image = await imagesApi.findByUserId(employee.User.id);
                return [employee.id, image] as [string, IImage];
            })
        ).then((entries) => setEmployeeImages(Object.fromEntries(entries)));
    }, [selectedStoreEmployees]);

  const handleSelectServiceClick = (serviceStoreId: string) =>
    navigate(`/service?id=${serviceStoreId}`);

  const handleSelectEmployeeClick = (employeeId: string) =>
    navigate(`/employee?id=${employeeId}`);

  const renderStoreInfo = (
    <Box sx={{ ...sharedStyles.verticalContent, gap: 3 }}>
      <Typography variant="h4" fontWeight="bold" textAlign="center">
        {store?.name}
      </Typography>

      {store?.images && store.images.length > 0 && (
        <Box width="100%">
                    <ImageGalleryContainerApi images={store.images} />
        </Box>
    )}

      {store?.description && store.description.length > 0 && (
        <Box width="100%">
          <CollapseWrapper
            label={t("storePage.descriptionLabel")}
            content={<RichTextRenderer htmlContent={store?.description} />}
          />
      </Box>
      )}
    </Box>
);

  const renderStoreLocation = (
    <Box sx={sharedStyles.verticalContent}>
    {isValidCoordinatesPair(store?.lat, store?.lng) && (
        <MapContainer
        zoom={17}
        center={[store.lat, store.lng]}
        style={{ height: "400px" }}
        dragging={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
      <Marker key={store.id} position={[store.lat, store.lng]} />
    </MapContainer>
)}

      <Box sx={sharedStyles.horizontalContent}>
        <NearMeIcon />
        <Typography textAlign="justify">{`${store?.logradouro}, ${
        store?.number || "s/n"
        } - ${store?.district}, ${store?.city} - ${
        store?.state
        }, ${store?.CEP}`}</Typography>
    </Box>
    </Box>
);

  const renderWorkingScale = (
    <Box sx={{ ...sharedStyles.verticalContent, gap: 5 }}>
      <Typography variant="h5" fontWeight="bold" textAlign="center">
          {t("storePage.workingHoursTitle")}
      </Typography>

    <TimeRangeInterpreter
        timeMonday={store?.timeMonday}
        timeTuesday={store?.timeTuesday}
        timeWednesday={store?.timeWednesday}
        timeThursday={store?.timeThursday}
        timeFriday={store?.timeFriday}
        timeSaturday={store?.timeSaturday}
        timeSunday={store?.timeSunday}
     />
    </Box>
);

  const renderOurServices = (
    <Box sx={styles.singleSection}>
      <Typography variant="h5" fontWeight="bold">
        {t("storePage.ourServicesTitle")}
      </Typography>
      {selectedStoreServiceStores?.length > 0 ? (
        <Box sx={styles.horizontalScrollSection}>
          {selectedStoreServiceStores.map(
            (serviceStore: IServiceStore, index) => (
              <Box
                key={index}
                onClick={() => handleSelectServiceClick(serviceStore.id)}
                        >
                <CustomCard
                photoURL={serviceStore.images?.[0]?.thumbnailUrl ?? defaultServiceImage}
                defaultPhoto={defaultServiceImage}
                header={`R$ ${priceFormatter(serviceStore.price)}`}
                primaryText={serviceStore.serviceName}
                secondaryText={serviceStore.serviceName}
                />
            </Box>
            )
        )}
        </Box>
      ) : (
        <Box>
          <Typography color="text.secondary">
            {t("storePage.noServicesFound")}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const renderOurEmployees = (
    <Box sx={styles.singleSection}>
      <Typography variant="h5" fontWeight="bold">
          {t("storePage.ourEmployeesTitle")}
      </Typography>
      {selectedStoreEmployees?.length > 0 ? (
        <Box sx={styles.horizontalScrollSection}>
          {selectedStoreEmployees.map((employee: IEmployee, index) => (
            <Box
              key={index}
              onClick={() => handleSelectEmployeeClick(employee.id)}
                    >
                    <CustomCard
                photoURL={employeeImages[employee.id]?.thumbnailUrl ?? defaultEmployeeImage}
                defaultPhoto={defaultEmployeeImage}
                header={""}
                primaryText={employee.User?.name}
                secondaryText={""}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Box>
          <Typography color="text.secondary">
            {t("storePage.noEmployeesFound")}
          </Typography>
        </Box>
      )}
    </Box>
);

  return (
    <Box
      sx={{
        overflowX: "hidden",
      }}
    >
      <ClientNavbar hideMidButton />

      <Box sx={backButtonContainer}>
        <BackPageButton />
      </Box>

      {store && (
        <Box
          sx={{
            ...sharedStyles.form,
            marginX: { xs: 3, md: 3, lg: 10 },
            marginBottom: 3,
          }}
        >
          {renderStoreInfo}
          {store && renderStoreLocation}

          <Divider />

          {renderWorkingScale}

          <Divider />

          {renderOurServices}

          {renderOurEmployees}
        </Box>
      )}
    </Box>
  );
};

export default StorePage;

const styles = {
  mainSectionContainer: {
    display: "flex",
    flexDirection: "column",
    margin: "50px 100px",
    gap: "80px",

    "@media(max-width:500px)": {
      margin: "50px 20px",
    },
  } as SxProps,

  storeImage: {
    width: "100%",
    height: "400px",
  } as SxProps,

  singleSection: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    alignItems: "center",
    width: "100%",
  } as SxProps,

  contactsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 1.5,
  } as SxProps,

  singleContact: { display: "flex", alignItems: "center", gap: 1 } as SxProps,

  horizontalScrollSection: {
    display: "flex",
    overflowX: "auto",
    whiteSpace: "nowrap",
    width: "100%",
    p: "10px 0px",
    gap: "20px",
  } as SxProps,
};
